import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMemo, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';

import { useMultiplePixelCostBreakdownVisualizationData } from '@/api/pixel';
import Flare, {
  Axis,
  type FlareSeriesOptions,
  Legend,
  Tooltip,
  linkifyAxisLabels,
  tooltipPositionBelow,
} from '@/components/Flare';
import AxisLabel from '@/components/Flare/guides/AxisLabel';
import Bar from '@/components/Flare/marks/Bar';
import { usePageFilterContext } from '@/components/page';
import { Panel } from '@/components/panels';
import { Tabs, TabsList, TabsTrigger } from '@/components/Tabs';
import { ALL_PRIMARY, AUDIENCE_PRIMARY, TAM_PRIMARY } from '@/constants/colors';
import { numberFormat } from '@/helper/numberFormatter';
import useInstanceNavigate from '@/hooks/useInstanceNavigate';
import { ROUTES } from '@/router';

type Props = {
  pixelIds?: string[];
};

const PixelMultiCostPerVisitChart = ({ pixelIds }: Props) => {
  const navigate = useInstanceNavigate();
  const { pageFilters } = usePageFilterContext();
  const { startDate, endDate } = pageFilters;
  const [chartMode, setChartMode] = useState('cpm');
  const { pixelCostBreakdownData, isLoading, error } =
    useMultiplePixelCostBreakdownVisualizationData(pixelIds, startDate, endDate);

  const options = [
    { value: 'cpm', label: t`CPM` },
    { value: 'cpv', label: t`Cost Per Visit` },
  ];
  const handleChartModeChange = (value: string | null) => {
    if (value === 'cpm' || value === 'cpv') {
      setChartMode(value);
    }
  };

  const allSeriesName = t`All`;
  const tamSeriesName = t`TAM`;
  const audienceSeriesName = t`Target Audience`;

  const sanitizedData: FlareSeriesOptions[] | undefined = useMemo(() => {
    if (!pixelCostBreakdownData) {
      return undefined;
    }

    const data = pixelCostBreakdownData.map((d) => (chartMode === 'cpm' ? d.cpm : d.cpv));

    return [
      {
        id: 'all',
        name: allSeriesName,
        zIndex: 1,
        data: data.map((d) => ({ amount: d.all })),
      },
      {
        id: 'tam',
        name: tamSeriesName,
        zIndex: 2,
        data: data.map((d) => ({ amount: d.tam })),
      },
      {
        id: 'audience',
        name: audienceSeriesName,
        zIndex: 3,
        data: data.map((d) => ({ amount: d.audience })),
      },
    ];
  }, [pixelCostBreakdownData, chartMode, allSeriesName, tamSeriesName, audienceSeriesName]);

  return (
    <Panel
      title={<Trans>Cost</Trans>}
      actions={
        <Tabs value={chartMode} onValueChange={handleChartModeChange}>
          <TabsList>
            {options.map((option) => (
              <TabsTrigger key={option.value} value={option.value}>
                {option.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      }
      noPadding
      style={{ height: 477 }}
    >
      <Flare
        data={sanitizedData}
        description={t`A comparison between Cost per visit of an optional Audience, Total Addressable Market (TAM), and everything else.`}
        colors={[ALL_PRIMARY, TAM_PRIMARY, AUDIENCE_PRIMARY]}
        isLoading={isLoading}
        error={error}
        postRender={(chart) => {
          linkifyAxisLabels(chart.xAxis[0], (url) => navigate(url));
        }}
      >
        <Legend reversed />
        <Bar
          y="amount"
          position="dodge"
          showDataLabels
          dataLabelFormat={(label) =>
            label.y === 0 ? '' : numberFormat(label.y, { isCurrency: true })
          }
          skeletonLoaderOptions={{ numSeries: 3, numCategories: pixelIds?.length || 3 }}
        />
        <Axis
          position="right"
          labelFormat={(item) => numberFormat(item.value, { isCurrency: true, precision: 0 })}
          gridlineWidth={0}
          baselineWidth={1}
          tickAmount={5}
        />
        <Axis
          position="bottom"
          categories={pixelCostBreakdownData?.map((d) => d.pixel.name || '') || []}
          labelFormat={(item) => {
            const { pixel } = pixelCostBreakdownData?.[item.pos] || {};
            if (!pixel?.id || !pixel?.name) {
              return '';
            }
            return renderToStaticMarkup(
              <AxisLabel
                align="right"
                label={pixel?.name}
                linkUrl={ROUTES.pixelById(pixel.id)}
                secondaryLabel={pixel?.audience?.name}
              />,
            );
          }}
          useHTML
          crosshair="rect"
          baselineWidth={0}
        />
        <Tooltip
          titleFormat={(point) => point?.key}
          rowValueFormat={(point) => numberFormat(point?.y, { isCurrency: true })}
          positioner={tooltipPositionBelow}
          shared
        />
      </Flare>
    </Panel>
  );
};

export default PixelMultiCostPerVisitChart;
